import { useTranslation } from "react-i18next";
import type {
  ContractStatusNotification as ContractStatusNotificationType,
  ContractRenewalInitiationType,
} from "@/gql/graphql";
import { NotificationReason } from "@/gql/graphql";
import { format } from "@/utils";
import DataListItemText from "@/components/app/Navigation/GlobalHeader/Notifications/Data/DataListItemText";
import { ErrorReason } from "@/gql/schema-types";

type NotificationProps = {
  data: Pick<
    ContractStatusNotificationType,
    | "contractEndDate"
    | "contractNumber"
    | "errorReason"
    | "notificationReason"
    | "renewalInitiationType"
  >;
};

const VALID_RENEWAL_REASONS = new Set<NotificationReason>([
  NotificationReason.ContractHasOutstandingItems,
  NotificationReason.RenewalRegular,
  NotificationReason.RenewalImpendingReminder,
  NotificationReason.AutoRenewalReminderFirst,
  NotificationReason.AutoRenewalReminderSecond,
  NotificationReason.RenewalUpcomingExpiry,
  NotificationReason.RenewalGeneralReminder,
  NotificationReason.RenewalExpiresToday,
  NotificationReason.RenewalLate,
  NotificationReason.RenewalLateExpired,
  NotificationReason.RenewalSubmitted,
  NotificationReason.RenewalInitiationError,
]);

const VALID_ERROR_REASONS = new Set<ErrorReason>([
  ErrorReason.QuoteCreationFailed,
  ErrorReason.QuoteCreationIsNotAllowed,
  ErrorReason.ContractOnSuspensionHold,
  ErrorReason.AmendmentInProgress,
]);

const getNotificationKey = (
  notificationReason: NotificationReason,
  errorReason?: ErrorReason | null,
  renewalInitiationType?: ContractRenewalInitiationType | null
): string => {
  if (errorReason === ErrorReason.AmendmentInProgress) {
    return errorReason;
  }
  if (errorReason && renewalInitiationType) {
    return VALID_ERROR_REASONS.has(errorReason)
      ? `${renewalInitiationType}_${errorReason}`
      : "unknown";
  }
  if (
    notificationReason === NotificationReason.RenewalInitiationError &&
    !errorReason
  ) {
    return "unknown_renewal_initiation_error";
  }
  if (VALID_RENEWAL_REASONS.has(notificationReason)) {
    return notificationReason;
  }

  return "unknown";
};
const ContractRenewalNotification = (props: NotificationProps) => {
  const { data } = props;
  const {
    notificationReason,
    contractNumber,
    contractEndDate,
    errorReason,
    renewalInitiationType,
  } = data;
  const { t } = useTranslation();
  // Format date for display
  const formattedContractEndDate = contractEndDate
    ? format.formatDate(contractEndDate)
    : undefined;
  const notificationKey = getNotificationKey(
    notificationReason,
    errorReason,
    renewalInitiationType
  ).toLowerCase();

  // Prepare translation data for title and description
  // All notification types use contractEndDate and contractNumber
  const translationData = {
    contractNumber,
    contractEndDate:
      formattedContractEndDate ?? t("notifications.contract_end_date_fallback"),
  };

  return (
    <DataListItemText
      primary={t(
        `notifications.renewal.${notificationKey}.title`,
        translationData
      )}
      secondary={t(
        `notifications.renewal.${notificationKey}.description`,
        translationData
      )}
    />
  );
};

export default ContractRenewalNotification;
